// Typed fetch client for the rocky-monitor Worker API (same-origin /api/*).
// Every route from the §12 API contract is represented here with a response type.
// Cookie auth: all calls send `credentials: "include"` so the HttpOnly rm_session
// cookie rides along. A 401 throws ApiError(401) so callers can redirect to /login.

// ── Error type ───────────────────────────────────────────────────────
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** True when the error is an auth failure (session missing/expired). */
export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

// ── Core fetch ───────────────────────────────────────────────────────
async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  const finalInit: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  };

  let res: Response;
  try {
    res = await fetch(path, finalInit);
  } catch (e) {
    throw new ApiError(0, `network error: ${(e as Error).message}`);
  }

  // 204 / empty body
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : res.statusText) || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

// ── Response types (mirror worker contract) ──────────────────────────

export type HealthStatus = "ok" | "warning" | "error";

export interface ServiceStatus {
  service: string;
  status: string; // ok | degraded | down (raw health string)
  httpCode: number | null;
  responseMs: number | null;
  ts: string;
  uptime24h: number | null; // 0..100, ok snapshots / total over last 24h (null if none)
  p50Ms: number | null; // median response_ms over last 24h (null if none)
  errors: number; // per-service worker errors today (UTC)
  reason: string; // first active alert message, else "all checks passing"
}

export interface StatusResponse {
  overall: { status: HealthStatus; reasons: string[] };
  services: ServiceStatus[];
}

export type CostCategory = "Workers" | "R2" | "D1" | "KV" | "DO";

export interface BillingResource {
  resource: string;
  category: CostCategory;
  label: string;
  projected: number;
  included: number;
  overageUsd: number;
  /** Fraction: 0.8 = 80%. */
  projectedPct: number;
  /** Month-to-date ACTUAL usage in native units. Optional — older workers omit it. */
  usage?: number;
  /** Fraction of included consumed so far (usage / included). Optional. */
  nowPct?: number;
}

export interface BillingCurrent {
  month: string; // YYYY-MM
  baseUsd: number;
  totalUsd: number;
  byCategory: Record<CostCategory, number>;
  resources: BillingResource[];
  budget: { capUsd: number; projectedPct: number };
  /** Base + overage on ACTUALS to date (no projection). Optional — older workers omit it. */
  billedSoFarUsd?: number;
  /** True when any cf_metric_hourly row this month was sampled (values are estimates). */
  sampled?: boolean;
}

/** One row from billing_month (history). */
export interface BillingMonthRow {
  month: string;
  base_usd: number;
  workers_usd: number | null;
  r2_usd: number | null;
  d1_usd: number | null;
  kv_usd: number | null;
  do_usd: number | null;
  total_usd: number | null;
  is_final: number; // 0 projected, 1 closed
}

/** One row from alert_log. */
export interface AlertRow {
  id: number;
  ts: string | null;
  severity: string | null; // warning | error
  service: string | null;
  kind: string | null; // down | http_5xx | quota | quota100 | budget | latency
  message: string | null;
  dedupe_key: string | null;
  resolved_ts: string | null;
}

export interface UsageRow {
  day: string; // "YYYY-MM-DD" (UTC)
  service: string;
  resource: string;
  total: number;
}

/** Per-service error rollup for a single day (GET /api/errors). */
export interface ServiceErrorRow {
  service: string;
  requests: number;
  errors: number;
  uptimePct: number | null;
}

/** Body for POST /api/pytha/contract (admin/seed upsert). */
export interface PythaContractBody {
  company_id: string;
  company_name: string;
  plan?: string;
  storage_allowance_gb?: number;
  render_allowance?: number;
  start_date?: string;
  expiry_date?: string;
  auto_renew?: boolean;
  rebill_rate?: number;
  contact_email?: string;
}

export interface DailySummaryRow {
  day: string;
  service: string;
  uptime_pct: number | null;
  total_requests: number | null;
  total_bytes: number | null;
  total_4xx: number | null;
  total_5xx: number | null;
  cache_hit_pct: number | null;
  status: string | null;
  incidents: number | null;
  fw_events: number | null;
}

export interface PythaCompany {
  company_id: string;
  company_name: string;
  plan: string | null;
  expiry_date: string | null;
  days_left: number | null;
  storage_gb: number;
  renders: number;
  jobs: number;
  rebill_usd: number;
  attributed_cost_usd: number;
}

/** One daily-usage row for a single company (GET /api/pytha/company/:id). */
export interface PythaDailyRow {
  day: string;
  company_id: string;
  r2_storage_gb: number;
  renders: number;
  requests: number;
  attributed_cost_usd: number;
  rebill_usd: number;
}

export interface MeResponse {
  user: string;
}

// ── Auth ─────────────────────────────────────────────────────────────
export const api = {
  login: (user: string, pass: string) =>
    request<{ ok: true }>("/api/login", { method: "POST", json: { user, pass } }),

  logout: () => request<{ ok: true }>("/api/logout", { method: "POST" }),

  me: () => request<MeResponse>("/api/me"),

  // ── Data routes ────────────────────────────────────────────────────
  status: () => request<StatusResponse>("/api/status"),

  billingCurrent: () => request<BillingCurrent>("/api/billing/current"),

  billingHistory: (months = 12) =>
    request<BillingMonthRow[]>(`/api/billing/history?months=${months}`),

  alerts: (active = true) =>
    request<AlertRow[]>(`/api/alerts${active ? "?active=1" : ""}`),

  usage: (range: "24h" | "7d" | "30d" = "24h") =>
    request<UsageRow[]>(`/api/usage?range=${range}`),

  errors: (day: string) =>
    request<ServiceErrorRow[]>(`/api/errors?day=${encodeURIComponent(day)}`),

  pythaCompanies: () => request<PythaCompany[]>("/api/pytha/companies"),

  pythaCompany: (id: string) =>
    request<PythaDailyRow[]>(`/api/pytha/company/${encodeURIComponent(id)}`),

  addPythaContract: (body: PythaContractBody) =>
    request<{ ok: true }>("/api/pytha/contract", { method: "POST", json: body }),

  renewContract: (id: string, termMonths: number, price: number) =>
    request<{ ok: true; expiry_date: string }>(
      `/api/pytha/contract/${encodeURIComponent(id)}/renew`,
      { method: "POST", json: { term_months: termMonths, price } },
    ),

  refresh: () => request<{ ok: true }>("/api/refresh", { method: "POST" }),
};
